import { useState, type FormEvent } from 'react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Spinner } from '@/components/ui/Spinner'
import { requireClient } from '@/lib/matrix/client'
import { useNavigate } from 'react-router'
import { useRoomStore } from '@/lib/store/room-store'
import { Visibility } from 'matrix-js-sdk'

interface RoomCreateDialogProps {
  onClose: () => void
}

export function RoomCreateDialog({ onClose }: RoomCreateDialogProps) {
  const [name, setName] = useState('')
  const [topic, setTopic] = useState('')
  const [isPrivate, setIsPrivate] = useState(true)
  const [encrypted, setEncrypted] = useState(true)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const { setActiveRoom } = useRoomStore()

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return

    setCreating(true)
    setError(null)
    try {
      const client = requireClient()
      const result = await client.createRoom({
        name: name.trim(),
        topic: topic.trim() || undefined,
        visibility: isPrivate ? Visibility.Private : Visibility.Public,
        initial_state: encrypted ? [
          {
            type: 'm.room.encryption',
            state_key: '',
            content: { algorithm: 'm.megolm.v1.aes-sha2' },
          },
        ] : [],
      })
      setActiveRoom(result.room_id)
      navigate(`/room/${encodeURIComponent(result.room_id)}`)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create room')
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full max-w-md bg-[rgb(var(--np-surface))] border border-[rgb(var(--np-border))] rounded-lg p-6 animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-[rgb(var(--np-tx-primary))] mb-4">Create Room</h2>

        <form onSubmit={handleCreate} className="space-y-4">
          <Input
            label="Room Name"
            placeholder="General"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            disabled={creating}
          />
          <Input
            label="Topic"
            placeholder="What's this room about?"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            disabled={creating}
          />

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-[rgb(var(--np-tx-secondary))] cursor-pointer">
              <input
                type="checkbox"
                checked={isPrivate}
                onChange={(e) => setIsPrivate(e.target.checked)}
                className="accent-[rgb(var(--np-accent))]"
              />
              Private room
            </label>
            <label className="flex items-center gap-2 text-sm text-[rgb(var(--np-tx-secondary))] cursor-pointer">
              <input
                type="checkbox"
                checked={encrypted}
                onChange={(e) => setEncrypted(e.target.checked)}
                className="accent-[rgb(var(--np-accent))]"
              />
              Encrypted
            </label>
          </div>

          {error && (
            <div className="border-l-2 border-l-[rgb(var(--np-status-critical))] bg-[rgb(var(--np-status-critical)/0.05)] px-4 py-2 rounded-r">
              <p className="text-xs text-[rgb(var(--np-status-critical))]">{error}</p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" type="button" onClick={onClose} disabled={creating}>
              Cancel
            </Button>
            <Button type="submit" disabled={creating || !name.trim()}>
              {creating ? <Spinner size={16} /> : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
